import React, { useState, useEffect, useRef } from 'react';
import enhancedApiClient from '../api/enhancedApiClient';

const TaskDetailModal = ({ task, onClose, eventId, currentUser, onStatusUpdate }) => {
  const [vendorName, setVendorName] = useState('');
  const [currentUserDisplayName, setCurrentUserDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [taskStatus, setTaskStatus] = useState('pending');
  const [subTasks, setSubTasks] = useState([]);
  const [newSubTask, setNewSubTask] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [comment, setComment] = useState('');
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const activityEndRef = useRef(null);

  const resolvedEventId = eventId || task?.PK?.replace('EVENT#', '') || task?.eventId;
  const isOwner = task?.createdByUserId === currentUser?.username || task?.createdByUserId === 'system';

  useEffect(() => {
    if (!task) return;
    setDescription(task.details || task.description || '');
    setSubTasks(task.subTasks || []);
    setAttachments(task.attachments || []);
    setTaskStatus(task.status || 'pending');
    setIsEditingDesc(false);
    setNewSubTask('');
    setComment('');

    const loadData = async () => {
      if (!resolvedEventId || !task.id) return;
      // Load vendor/creator name (who created this task)
      if (task.createdByUserId && task.createdByUserId !== 'system') {
        try {
          const profile = await enhancedApiClient.getUser(task.createdByUserId);
          if (profile?.currentVendorId) {
            const vRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${profile.currentVendorId}`);
            if (vRes.ok) {
              const vData = await vRes.json();
              setVendorName(vData.name?.S || vData.name || profile.name || 'Unknown');
            } else {
              setVendorName(profile?.name || 'Unknown');
            }
          } else {
            setVendorName(profile?.name || 'Unknown');
          }
        } catch { setVendorName('Unknown'); }
      } else if (task.createdByUserId === 'system') {
        setVendorName('Your Planning Guide');
      }

      // Load current user's display name
      if (currentUser?.username) {
        try {
          const myProfile = await enhancedApiClient.getUser(currentUser.username);
          if (myProfile?.userType === 'client') {
            // For clients, use the event's clientName
            const eventRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${resolvedEventId}`);
            if (eventRes.ok) {
              const eventData = await eventRes.json();
              setCurrentUserDisplayName(eventData.clientName || myProfile?.name || currentUser.username);
            } else {
              setCurrentUserDisplayName(myProfile?.name || currentUser.username);
            }
          } else if (myProfile?.currentVendorId) {
            const vRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${myProfile.currentVendorId}`);
            if (vRes.ok) {
              const vData = await vRes.json();
              setCurrentUserDisplayName(vData.name?.S || vData.name || myProfile.name || currentUser.username);
            } else {
              setCurrentUserDisplayName(myProfile?.name || currentUser.username);
            }
          } else {
            setCurrentUserDisplayName(myProfile?.name || currentUser.username);
          }
        } catch { setCurrentUserDisplayName(currentUser.username); }
      }

      // Load activities
      try {
        const acts = await enhancedApiClient.makeRequest(`/events/${resolvedEventId}/tasks/${task.id}/activities`);
        setActivities(acts || []);
        setTimeout(() => activityEndRef.current?.scrollIntoView({ behavior: 'auto' }), 200);
      } catch { setActivities([]); }
    };
    loadData();
  }, [task?.id, resolvedEventId]);

  const saveField = async (updates) => {
    try {
      await enhancedApiClient.updateTask(resolvedEventId, task.id, updates);
      onStatusUpdate();
    } catch (e) {
      console.error('Failed to save:', e);
    }
  };

  const addActivity = async (action) => {
    try {
      const activity = {
        action,
        userId: currentUser?.username,
        userName: currentUserDisplayName || currentUser?.username,
        timestamp: new Date().toISOString()
      };
      await enhancedApiClient.makeRequest(`/events/${resolvedEventId}/tasks/${task.id}/activities`, {
        method: 'POST',
        body: JSON.stringify(activity)
      });
      setActivities(prev => [...prev, activity]);
      setTimeout(() => activityEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Failed to log activity:', e);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    await addActivity(`commented: ${comment.trim()}`);
    setComment('');
  };

  const handleToggleSubTask = async (index) => {
    const updated = subTasks.map((st, i) => i === index ? { ...st, completed: !st.completed } : st);
    setSubTasks(updated);
    await saveField({ subTasks: updated });
    const st = updated[index];
    await addActivity(`marked "${st.text}" ${st.completed ? 'complete' : 'incomplete'}`);
  };

  const handleAddSubTask = async () => {
    if (!newSubTask.trim()) return;
    const st = { id: `st-${Date.now()}`, text: newSubTask.trim(), completed: false };
    const updated = [...subTasks, st];
    setSubTasks(updated);
    setNewSubTask('');
    await saveField({ subTasks: updated });
    await addActivity(`added sub-task "${st.text}"`);
  };

  const handleDeleteSubTask = async (index) => {
    const removed = subTasks[index];
    const updated = subTasks.filter((_, i) => i !== index);
    setSubTasks(updated);
    await saveField({ subTasks: updated });
    await addActivity(`removed sub-task "${removed.text}"`);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10MB'); return; }
    setUploading(true);
    try {
      const { fileUrl, fileName } = await enhancedApiClient.uploadFile(file);
      const att = { fileUrl, fileName, uploadedBy: currentUser?.username, timestamp: new Date().toISOString() };
      const updated = [...attachments, att];
      setAttachments(updated);
      await saveField({ attachments: updated });
      await addActivity(`attached ${fileName}`);
    } catch (e) {
      console.error('Upload failed:', e);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveDescription = async () => {
    setIsEditingDesc(false);
    await saveField({ details: description, description });
    await addActivity('updated description');
  };

  const handleStatusToggle = async () => {
    const newStatus = taskStatus === 'completed' ? 'pending' : 'completed';
    setTaskStatus(newStatus);
    await saveField({ status: newStatus });
    await addActivity(`marked task ${newStatus}`);
    onStatusUpdate();
  };

  const subTaskProgress = subTasks.length > 0 ? Math.round((subTasks.filter(s => s.completed).length / subTasks.length) * 100) : 0;

  useEffect(() => {
    if (task) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [task]);

  if (!task) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '960px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0
        }}>
          <div
            onClick={handleStatusToggle}
            style={{
              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
              backgroundColor: taskStatus === 'completed' ? '#6B9080' : 'white',
              border: taskStatus === 'completed' ? 'none' : '2px solid #D1D5DB',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px'
            }}
          >
            {taskStatus === 'completed' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12"/></svg>}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif' }}>
              {task.name}
            </h2>
            <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
              Added by {vendorName} {task.dueDate && <span> · Due {new Date(task.dueDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
              {isEditingDueDate ? (
                <span style={{ marginLeft: '8px' }}>
                  <input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)}
                    style={{ padding: '2px 6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '12px' }} />
                  <button onClick={async () => { await saveField({ dueDate: newDueDate }); task.dueDate = newDueDate; setIsEditingDueDate(false); await addActivity(`changed due date to ${newDueDate}`); }}
                    style={{ marginLeft: '4px', padding: '2px 8px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => setIsEditingDueDate(false)}
                    style={{ marginLeft: '4px', padding: '2px 8px', backgroundColor: '#E5E7EB', color: '#6B7280', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
                </span>
              ) : (
                <button onClick={() => { setNewDueDate(task.dueDate || ''); setIsEditingDueDate(true); }}
                  style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#6B9080', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', display: isOwner ? 'inline' : 'none' }}>
                  Edit date
                </button>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B7280', padding: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Two-column body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left column */}
          <div style={{ flex: 3, overflowY: 'auto', padding: '24px', borderRight: '1px solid #E5E7EB' }}>

            {/* Description */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.5"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Description</span>
                </div>
                {!isEditingDesc && isOwner && (
                  <button onClick={() => setIsEditingDesc(true)}
                    style={{ padding: '4px 12px', backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    Edit
                  </button>
                )}
              </div>
              {isEditingDesc ? (
                <div>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
                    style={{ width: '100%', padding: '12px', border: '1px solid #6B9080', borderRadius: '8px', fontSize: '14px', resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
                    autoFocus placeholder="Add a description..." />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button onClick={handleSaveDescription}
                      style={{ padding: '6px 16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>Save</button>
                    <button onClick={() => { setDescription(task.details || task.description || ''); setIsEditingDesc(false); }}
                      style={{ padding: '6px 16px', backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => isOwner && setIsEditingDesc(true)} style={{ fontSize: '14px', color: description ? '#374151' : '#9CA3AF', lineHeight: '1.6', cursor: isOwner ? 'pointer' : 'default', padding: '8px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px', minHeight: '40px' }}>
                  {description || 'Add a description...'}
                </div>
              )}
            </div>

            {/* Attachments */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.5"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Attachments</span>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  style={{ marginLeft: 'auto', padding: '4px 12px', backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? 'Uploading...' : '+ Add'}
                </button>
              </div>
              {attachments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {attachments.map((att, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1F2937' }}>{att.fileName}</div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{att.timestamp ? new Date(att.timestamp).toLocaleDateString() : ''}</div>
                      </div>
                      <button onClick={() => window.open(att.fileUrl, '_blank')}
                        style={{ padding: '4px 10px', backgroundColor: 'white', color: '#6B9080', border: '1px solid #6B9080', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>View</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', textAlign: 'center' }}>
                  No attachments yet
                </div>
              )}
              {task.documentUrl && !attachments.some(a => a.fileUrl === task.documentUrl) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px', marginTop: '8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#1F2937' }}>{task.documentUrl.split('/').pop().split('-').slice(1).join('-') || 'Document'}</div>
                    <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Original document</div>
                  </div>
                  <button onClick={() => window.open(task.documentUrl, '_blank')}
                    style={{ padding: '4px 10px', backgroundColor: 'white', color: '#6B9080', border: '1px solid #6B9080', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>View</button>
                </div>
              )}
            </div>

            {/* Sub-tasks */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Sub-tasks</span>
              </div>
              {subTasks.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>{subTaskProgress}%</span>
                    <div style={{ flex: 1, height: '6px', backgroundColor: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${subTaskProgress}%`, height: '100%', backgroundColor: '#6B9080', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {subTasks.map((st, i) => (
                  <div key={st.id || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <div
                      onClick={() => handleToggleSubTask(i)}
                      style={{
                        width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0, cursor: 'pointer',
                        backgroundColor: st.completed ? '#6B9080' : 'white',
                        border: st.completed ? 'none' : '2px solid #D1D5DB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      {st.completed && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12"/></svg>}
                    </div>
                    <span style={{ flex: 1, fontSize: '14px', color: st.completed ? '#9CA3AF' : '#374151', textDecoration: st.completed ? 'line-through' : 'none' }}>
                      {st.text}
                    </span>
                    <button onClick={() => handleDeleteSubTask(i)}
                      style={{ background: 'none', border: 'none', color: '#D1D5DB', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}
                      onMouseOver={(e) => e.target.style.color = '#DC2626'}
                      onMouseOut={(e) => e.target.style.color = '#D1D5DB'}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <input
                  type="text" value={newSubTask} onChange={(e) => setNewSubTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubTask()}
                  placeholder="Add an item"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                />
                <button onClick={handleAddSubTask}
                  style={{ padding: '8px 16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>Add</button>
              </div>
            </div>

            {/* Footer action */}
            <button onClick={handleStatusToggle}
              style={{
                width: '100%', padding: '12px', backgroundColor: '#6B9080', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}>
              {taskStatus === 'completed' ? 'Mark as Pending' : 'Mark as Complete'}
            </button>
          </div>

          {/* Right column — Comments & Activity */}
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Comments and activity</span>
              </div>
            </div>

            {/* Comment input */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text" value={comment} onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  placeholder="Write a comment..."
                  style={{ flex: 1, padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                />
                {comment.trim() && (
                  <button onClick={handleAddComment}
                    style={{ padding: '8px 14px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Send</button>
                )}
              </div>
            </div>

            {/* Activity feed */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
              {activities.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {activities.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((act, i) => {
                    const isComment = act.action?.startsWith('commented:');
                    const isMe = act.userId === currentUser?.username || act.userName === currentUser?.username;
                    const displayName = isMe ? 'You' : (act.userName || act.userId || 'Someone');
                    // For initials: resolve old usernames to proper names
                    let rawName = act.userName || act.userId || '??';
                    if (isMe) rawName = currentUserDisplayName || 'You';
                    else if (rawName === 'testclient1' || rawName === 'Test Client One') rawName = 'Sam Taylor';
                    else if (rawName === 'testvenue1-user' || rawName === 'Test Venue Manager') rawName = 'Orakei Bay';
                    else if (rawName === 'testvendor1-user' || rawName === 'Test Vendor Manager') rawName = 'Sakshins Flower Shop';
                    const initials = rawName.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase() || '??';

                    return (
                      <div key={i} style={{ display: 'flex', gap: '10px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                          backgroundColor: '#6B9080', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '11px', fontWeight: '600'
                        }}>
                          {initials}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', color: '#1F2937' }}>
                            <span style={{ fontWeight: '600' }}>{displayName}</span>{' '}
                            {isComment ? (
                              <span style={{ fontWeight: '400' }}>{act.action.replace('commented: ', '')}</span>
                            ) : (
                              <span style={{ color: '#6B7280' }}>{act.action}</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6B9080', marginTop: '2px' }}>
                            {new Date(act.timestamp).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(act.timestamp).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={activityEndRef} />
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '13px', padding: '40px 0' }}>
                  No activity yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
